import { withStyleSheetOverride } from '@klarna/higher-order-components'
import { selectors as coreSelectors, components as coreComponents } from 'mage-core'
import { ButtonQuaternary, Input } from 'mage-deps/@klarna/bubble-ui'
import { Modal } from 'mage-deps/@klarna/mp-ui'
import { selectors as i18nSelectors } from 'mage-i18n'
import { thunks as stateThunks } from 'mage-state'
import PropTypes from 'prop-types'
import * as R from 'ramda'
import React, { useState } from 'react'
import { connect } from 'react-redux'

import { saveDetails, resetPassword } from '../../../actions'

const getDefaultSyleSheet = () => ({
  email: {
    marginTop: 20
  },
  row: {
    display: 'flex',
    marginTop: 20
  },
  col: {
    flex: 1
  },
  'col:last-child': {
    marginLeft: 20
  },
  status: {
    marginTop: 20
  },
  button: {
    marginTop: 20,
    textAlign: 'center'
  }
})

const UpdateProfile = ({
  userId,
  email,
  givenName,
  familyName,
  styleSheet,
  isUpdateProfileLoading,
  isUpdateProfileFailed,
  t,
  onClose,
  saveDetails,
  resetPassword,
  refreshToken
}) => {
  const [givenNameState, updateGivenName] = useState(givenName)
  const [familyNameState, updateFamilyName] = useState(familyName)
  const [hasResetPassword, setHasResetPassword] = useState(false)
  const [resetPasswordLabel, setResetPasswordLabel] = useState(t('core.updateProfile.resetPasswordLabel'))

  return (
    <Modal
      id='update-profile'
      onClose={onClose}
      title={t('core.updateProfile.title')}
      generalError={isUpdateProfileFailed ? t('core.updateProfile.error') : undefined}
      buttonPrimary={{
        onClick: async () => {
          try {
            await saveDetails(userId, {
              given_name: givenNameState,
              family_name: familyNameState
            })
            await refreshToken({ force: true })
            onClose()
          } catch (err) {}
        },
        text: t('core.updateProfile.submit'),
        loading: isUpdateProfileLoading
      }}
    >
      <form>
        <div style={styleSheet.email}>
          <Input
            disabled
            label={t('core.updateProfile.emailLabel')}
            name='email'
            value={email}
          />
        </div>
        <div style={styleSheet.row}>
          <div style={styleSheet.col}>
            <Input
              style={styleSheet.input}
              label={t('core.updateProfile.firstNameLabel')}
              name='given_name'
              value={givenNameState}
              onChange={updateGivenName}
            />
          </div>
          <div style={{
            ...styleSheet.col,
            ...styleSheet['col:last-child']
          }}
          >
            <Input
              style={styleSheet.input}
              label={t('core.updateProfile.lastNameLabel')}
              name='family_name'
              value={familyNameState}
              onChange={updateFamilyName}
            />
          </div>
        </div>
      </form>
      <div style={styleSheet.row}>
        <ButtonQuaternary
          size='small'
          onClick={() => {
            setHasResetPassword(true)
            resetPassword(userId)
              .then(() => {
                return setResetPasswordLabel(t('core.updateProfile.resetPasswordSuccessLabel'))
              })
              .catch(() => {
                return setResetPasswordLabel(t('core.updateProfile.resetPasswordErrorLabel'))
              })
          }}
          disabled={hasResetPassword}
        >
          {resetPasswordLabel}
        </ButtonQuaternary>
      </div>
    </Modal>
  )
}

UpdateProfile.propTypes = {
  userId: PropTypes.string.isRequired,
  email: PropTypes.string.isRequired,
  givenName: PropTypes.string,
  familyName: PropTypes.string,
  styleSheet: PropTypes.shape({
    email: PropTypes.object.isRequired,
    col: PropTypes.object.isRequired,
    'col:last-child': PropTypes.object.isRequired,
    row: PropTypes.object.isRequired,
    input: PropTypes.object.isRequired
  }),
  isUpdateProfileLoading: PropTypes.bool,
  isUpdateProfileFailed: PropTypes.bool,
  t: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  saveDetails: PropTypes.func.isRequired,
  resetPassword: PropTypes.func,
  refreshToken: PropTypes.func.isRequired
}

UpdateProfile.defaultProps = {
  styleSheet: {
    email: {},
    col: {},
    'col:last-child': {},
    row: {},
    input: {}
  },
  isUpdateProfileLoading: false,
  isUpdateProfileFailed: false
}

export default R.compose(
  coreComponents.withTokenHelper(tokenHelper => ({
    userId: tokenHelper.getUserId(),
    email: tokenHelper.getEmail(),
    givenName: tokenHelper.getGivenName(),
    familyName: tokenHelper.getFamilyName()
  })),
  connect(
    state => ({
      isUpdateProfileLoading: coreSelectors.isUpdateProfileLoading(state),
      isUpdateProfileFailed: coreSelectors.isUpdateProfileFailed(state),
      t: i18nSelectors.translator(state)
    }),
    {
      saveDetails,
      resetPassword,
      refreshToken: stateThunks.refreshToken
    }
  ),
  withStyleSheetOverride(getDefaultSyleSheet)
)(UpdateProfile)

export {
  UpdateProfile
}
